// Frame drawing utilities - ported from original HTML
// All frame styles: none, eggs, flowers, bunnies, rainbow, canva_pa2026

// Frame picker options used by the React UI.
// Keep these IDs aligned with `drawFrame()` so the selector and renderer stay in sync.
export const FRAMES = [
  { id: 'none', label: '🚫 No Frame' },
  { id: 'eggs', label: '🥚 Easter Eggs' },
  { id: 'flowers', label: '🌸 Flowers' },
  { id: 'bunnies', label: '🐰 Bunnies' },
  { id: 'rainbow', label: '🌈 Rainbow' },
  { id: 'canva_pa2026', label: '🎨 PA 2026 Frame', type: 'image', src: '/frames/canva_pa2026.png' }
]

// Image cache for preloaded image frames
const imageCache = new Map()

// Preload all image frames
export function preloadImageFrames() {
  FRAMES.forEach(frame => {
    if (frame.type === 'image' && frame.src) {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.src = frame.src
      imageCache.set(frame.src, img)
    }
  })
}

// Get cached image (returns null if not loaded yet)
function getCachedImage(src) {
  const img = imageCache.get(src)
  if (!img) return null
  if (!img.complete) return null
  return img
}

const ECOLS = [
  ['#f9a8d4', '#f3e8ff'], // pink/purple
  ['#93c5fd', '#dbeafe'], // blue
  ['#86efac', '#dcfce7'], // green
  ['#fde68a', '#fef9c3'], // yellow
  ['#c4b5fd', '#ede9fe'], // purple
  ['#fed7aa', '#fff7ed']  // orange
]

function drawEgg(ctx, cx, cy, rx, ry, c1, c2) {
  ctx.save()
  ctx.translate(cx, cy)
  ctx.beginPath()
  ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2)
  ctx.clip()
  ctx.fillStyle = c1
  ctx.fillRect(-rx, -ry, rx * 2, ry * 2)
  ctx.fillStyle = c2
  ctx.fillRect(-rx, -ry * 0.2, rx * 2, ry * 0.4)
  // dots
  ctx.fillStyle = c1
  for (const dx of [-rx * .45, 0, rx * .45]) {
    ctx.beginPath()
    ctx.arc(dx, ry * .3, ry * .08, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.restore()
}

export function drawEggsFrame(ctx, W, H, bw) {
  const rx = bw * .68, ry = bw * .9
  const n = Math.max(3, Math.round(W / (rx * 2.8)))
  for (let i = 0; i < n; i++) {
    const cx = (i + .5) * (W / n)
    const [c1, c2] = ECOLS[i % ECOLS.length]
    const [d1, d2] = ECOLS[(i + 3) % ECOLS.length]
    drawEgg(ctx, cx, bw * 1.45 + ry, rx, ry, c1, c2)
    drawEgg(ctx, cx, H - bw * 1.45 - ry, rx, ry, d1, d2)
  }
  const nS = Math.max(2, Math.round((H - bw * 6) / (rx * 2.8)))
  for (let i = 0; i < nS; i++) {
    const cy = bw * 3.5 + i * ((H - bw * 7) / Math.max(1, nS - 1))
    const [c1, c2] = ECOLS[(i + 1) % ECOLS.length]
    const [d1, d2] = ECOLS[(i + 4) % ECOLS.length]
    ctx.save()
    ctx.translate(bw * 1.5, cy)
    ctx.rotate(Math.PI / 2)
    drawEgg(ctx, 0, 0, rx * .8, ry * .8, c1, c2)
    ctx.restore()
    ctx.save()
    ctx.translate(W - bw * 1.5, cy)
    ctx.rotate(Math.PI / 2)
    drawEgg(ctx, 0, 0, rx * .8, ry * .8, d1, d2)
    ctx.restore()
  }
}

function drawFlower(ctx, cx, cy, r, petal, center) {
  ctx.save()
  ctx.translate(cx, cy)
  for (let i = 0; i < 6; i++) {
    ctx.save()
    ctx.rotate((i / 6) * Math.PI * 2)
    ctx.beginPath()
    ctx.ellipse(0, -r * .6, r * .32, r * .48, 0, 0, Math.PI * 2)
    ctx.fillStyle = petal
    ctx.fill()
    ctx.restore()
  }
  ctx.beginPath()
  ctx.arc(0, 0, r * .28, 0, Math.PI * 2)
  ctx.fillStyle = center
  ctx.fill()
  ctx.restore()
}

export function drawFlowersFrame(ctx, W, H, bw) {
  const FLOWERS = [
    ['#f9a8d4', '#fde68a'],
    ['#86efac', '#fde68a'],
    ['#93c5fd', '#fde68a'],
    ['#c4b5fd', '#86efac']
  ]
  // Corners
  const corners = [
    [bw * 1.5, bw * 1.6],
    [W - bw * 1.5, bw * 1.6],
    [bw * 1.5, H - bw * 1.6],
    [W - bw * 1.5, H - bw * 1.6]
  ]
  corners.forEach(([cx, cy], i) => drawFlower(ctx, cx, cy, bw * 1.1, FLOWERS[i][0], FLOWERS[i][1]))
  
  // Mid-edge flowers
  const midEdges = [
    [W * .33, bw * 1.6],
    [W * .67, bw * 1.6],
    [W * .33, H - bw * 1.6],
    [W * .67, H - bw * 1.6]
  ]
  midEdges.forEach(([cx, cy], i) => drawFlower(ctx, cx, cy, bw * .72, FLOWERS[(i + 1) % 4][0], '#fff'))
  
  // Stems
  ctx.save()
  ctx.strokeStyle = '#86efac'
  ctx.lineWidth = bw * .11
  ctx.setLineDash([bw * .25, bw * .18])
  ;[bw * 1.5, W - bw * 1.5].forEach(x => {
    ctx.beginPath()
    ctx.moveTo(x, bw * 3)
    ctx.lineTo(x, H - bw * 3)
    ctx.stroke()
  })
  ctx.restore()
}

function drawBunny(ctx, cx, cy, size, col) {
  ctx.save()
  ctx.translate(cx, cy)
  ctx.fillStyle = col
  // Body
  ctx.beginPath()
  ctx.ellipse(0, size * .2, size * .38, size * .48, 0, 0, Math.PI * 2)
  ctx.fill()
  // Head
  ctx.beginPath()
  ctx.arc(0, -size * .12, size * .26, 0, Math.PI * 2)
  ctx.fill()
  // Left ear
  ctx.save()
  ctx.translate(-size * .11, -size * .55)
  ctx.rotate(-0.18)
  ctx.fillStyle = col
  ctx.beginPath()
  ctx.ellipse(0, 0, size * .09, size * .28, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#fca5a5'
  ctx.beginPath()
  ctx.ellipse(0, 0, size * .045, size * .16, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
  // Right ear
  ctx.save()
  ctx.translate(size * .11, -size * .55)
  ctx.rotate(0.18)
  ctx.fillStyle = col
  ctx.beginPath()
  ctx.ellipse(0, 0, size * .09, size * .28, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#fca5a5'
  ctx.beginPath()
  ctx.ellipse(0, 0, size * .045, size * .16, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
  // Nose
  ctx.fillStyle = '#fca5a5'
  ctx.beginPath()
  ctx.arc(0, -size * .08, size * .035, 0, Math.PI * 2)
  ctx.fill()
  // Tail
  ctx.fillStyle = '#fff'
  ctx.beginPath()
  ctx.arc(size * .36, size * .38, size * .1, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

export function drawBunniesFrame(ctx, W, H, bw) {
  const BCOLS = ['#e9d5ff', '#fce7f3', '#dbeafe', '#dcfce7']
  const positions = [
    [bw * 1.7, bw * 2.6],
    [W - bw * 1.7, bw * 2.6],
    [bw * 1.7, H - bw * 2.6],
    [W - bw * 1.7, H - bw * 2.6]
  ]
  positions.forEach(([cx, cy], i) => drawBunny(ctx, cx, cy, bw * 1.8, BCOLS[i]))
  
  // Dotted border
  ctx.save()
  ctx.strokeStyle = '#c4b5fd'
  ctx.lineWidth = bw * .12
  ctx.setLineDash([bw * .22, bw * .15])
  ctx.strokeRect(bw * .6, bw * .6, W - bw * 1.2, H - bw * 1.2)
  ctx.restore()
}

export function drawRainbowFrame(ctx, W, H, bw) {
  const COLS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6']
  const sw = bw / COLS.length
  COLS.forEach((col, i) => {
    ctx.fillStyle = col
    const off = i * sw
    ctx.fillRect(0, off, W, sw)
    ctx.fillRect(0, H - off - sw, W, sw)
    ctx.fillRect(off, 0, sw, H)
    ctx.fillRect(W - off - sw, 0, sw, H)
  })
}

export function drawBanner(ctx, W, H, bw, churchName) {
  // Top banner
  ctx.save()
  ctx.fillStyle = 'rgba(255,255,255,0.88)'
  ctx.fillRect(0, 0, W, bw * 2.8)
  ctx.fillStyle = '#5a1d9a'
  ctx.font = `900 ${bw * 1.05}px Nunito, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(churchName, W / 2, bw * 1.4)
  ctx.restore()
  
  // Bottom banner
  ctx.save()
  ctx.fillStyle = 'rgba(255,255,255,0.88)'
  ctx.fillRect(0, H - bw * 2.8, W, bw * 2.8)
  ctx.fillStyle = '#5a1d9a'
  ctx.font = `900 ${bw * 1.1}px Nunito, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('Happy Easter!', W / 2, H - bw * 1.4)
  ctx.restore()
}

export function drawFrame(ctx, W, H, style, churchName) {
  const bw = Math.round(W * 0.033)

  // No frame: draw only the church name at the top, no background
  if (style === 'none') {
    ctx.save()
    ctx.font = `900 ${bw * 1.05}px Nunito, sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.shadowColor = 'rgba(255,255,255,0.85)'
    ctx.shadowBlur = 6
    ctx.fillStyle = '#5a1d9a'
    ctx.fillText(churchName, W / 2, bw * 1.4)
    ctx.restore()
    return
  }

  
  // Check if this is an image-based frame
  const frameConfig = FRAMES.find(f => f.id === style)
  if (frameConfig?.type === 'image' && frameConfig.src) {
    const img = getCachedImage(frameConfig.src)
    if (img) {
      drawImageToFit(ctx, img, W, H)
    } else {
      // Fallback: draw banner if image not loaded yet
      drawBanner(ctx, W, H, bw, churchName)
    }
    return
  }
  
  drawBanner(ctx, W, H, bw, churchName)
  if (style === 'eggs') drawEggsFrame(ctx, W, H, bw)
  else if (style === 'flowers') drawFlowersFrame(ctx, W, H, bw)
  else if (style === 'bunnies') drawBunniesFrame(ctx, W, H, bw)
  else drawRainbowFrame(ctx, W, H, bw)
}

function drawImageToFit(ctx, img, W, H) {
  // Calculate scaling to cover the entire canvas while maintaining aspect ratio
  const scale = Math.max(W / img.width, H / img.height)
  const scaledWidth = img.width * scale
  const scaledHeight = img.height * scale
  const x = (W - scaledWidth) / 2
  const y = (H - scaledHeight) / 2
  ctx.drawImage(img, x, y, scaledWidth, scaledHeight)
}
